import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { useParams } from 'react-router-dom';
import type { OrganizationId } from '@kirin/api-client/contracts/organizations';
import {
  PolicyExceptionAction,
  type PolicyException,
} from '@kirin/api-client/contracts/policy-exceptions';
import {
  PolicyMode,
  type PolicyModeType,
  type PolicySettings,
} from '@kirin/api-client/contracts/policies';
import { ApiError } from '@kirin/api-client/errors';
import { PolicySettingsActions } from '../../components/PolicySettingsActions';
import { AllowBlockPolicyEditor } from './AllowBlockPolicyEditor';
import { TrustedMcpServersPanel } from './TrustedMcpServersPanel';
import {
  policyExceptionToPolicyRule,
  policyRuleToExceptionRuleObject,
} from '../lib/adapters';
import { defaultAllowBlockState } from '../config';
import { allowBlockStatesEqual } from '../lib/allowBlockStateCompare';
import { buildPolicySettingsPayload } from '../lib/buildPolicySettingsPayload';
import { dropPendingDeletes } from '../lib/policyRuleHelpers';
import type { AllowBlockPolicyKey, AllowBlockState } from '../types';
import type { UsePolicySettingsHook } from '../../types';
import { useToast } from '@/hooks/use-toast';
import { useUnsavedChangeActions } from '@/hooks/useScopeChangeHooks';
import {
  useCreateOrganizationPolicyException,
  useDeleteOrganizationPolicyException,
  useOrganizationPolicyExceptions,
  useUpdateOrganizationPolicyException,
} from '@/hooks/api/useOrganizationPolicyExceptions';
import {
  useCreateUserPolicyException,
  useDeleteUserPolicyException,
  useUpdateUserPolicyException,
  useUserPolicyExceptions,
} from '@/hooks/api/useUserPolicyExceptions';

function cloneAllowBlockState(state: AllowBlockState): AllowBlockState {
  return JSON.parse(JSON.stringify(state)) as AllowBlockState;
}

const statesEqual = allowBlockStatesEqual;

function getApiErrorStatus(error: unknown): number | undefined {
  if (error instanceof ApiError) {
    return error.status;
  }
  return undefined;
}

/** Debounce so we do not persist on every keystroke while editing a cell. */
const AUTO_PERSIST_DEBOUNCE_MS = 350;

function buildStateFromPolicyExceptions(
  policyKey: AllowBlockPolicyKey,
  blockedListMode: PolicyModeType,
  allowed: PolicyException[] | undefined,
  blocked: PolicyException[] | undefined
): AllowBlockState {
  return {
    blockedListMode,
    allowed: (allowed ?? []).map(exception =>
      policyExceptionToPolicyRule(policyKey, exception)
    ),
    blocked: (blocked ?? []).map(exception =>
      policyExceptionToPolicyRule(policyKey, exception)
    ),
  };
}

/** Prefer server value when present. */
function blockedListModeFromPolicySettings(
  settingsData: PolicySettings | undefined
): PolicyModeType | undefined {
  const entry = settingsData?.settings?.find(s => s.key === 'blockedListMode');
  const v = entry?.value;
  if (
    v === PolicyMode.Off ||
    v === PolicyMode.Monitor ||
    v === PolicyMode.Enforce
  ) {
    return v;
  }
  return undefined;
}

export interface AllowBlockPolicySettingsFormProps {
  policyKey: AllowBlockPolicyKey;
  usePolicySettings: UsePolicySettingsHook;
  saving: boolean;
  /**
   * Not used — allow/block rows persist via allow/block rule APIs and debounced
   * auto-save. Optional so callers that branch from `PolicySettingsForm` are not
   * forced to pass a no-op save handler.
   */
  onSave?: (
    policyKey: string,
    settings: Record<string, unknown>
  ) => void | Promise<void>;
  onReset: (policyKey: string) => void | Promise<void>;
  readOnly?: boolean;
  /** Support role: rows stay viewable/expandable, only their commit actions
   *  (row Save, Remove, Restore) are disabled. Persistence itself (auto-save,
   *  blocked-list mode changes, Reset) stays gated on `readOnly` alone, which
   *  callers must keep `true` whenever this is `true`. */
  isSupportReadOnly?: boolean;
  onPolicySettingsDirty?: (dirty: boolean) => void;
  scope?: string;
}

export function AllowBlockPolicySettingsForm({
  policyKey,
  usePolicySettings,
  saving,
  onSave,
  onReset,
  readOnly = false,
  isSupportReadOnly = false,
  onPolicySettingsDirty,
  scope,
}: AllowBlockPolicySettingsFormProps): ReactElement {
  const { toast } = useToast();
  const { markDirty, markClean } = useUnsavedChangeActions();
  const { organizationId } = useParams<{ organizationId?: string }>();
  const isOrgScope = Boolean(organizationId);
  const { data: settingsData, isLoading } = usePolicySettings(policyKey);

  const orgAllowedExceptions = useOrganizationPolicyExceptions(
    organizationId as OrganizationId | undefined,
    policyKey,
    PolicyExceptionAction.Allow,
    isOrgScope
  );
  const orgBlockedExceptions = useOrganizationPolicyExceptions(
    organizationId as OrganizationId | undefined,
    policyKey,
    PolicyExceptionAction.Block,
    isOrgScope
  );
  const userAllowedExceptions = useUserPolicyExceptions(
    policyKey,
    PolicyExceptionAction.Allow,
    !isOrgScope
  );
  const userBlockedExceptions = useUserPolicyExceptions(
    policyKey,
    PolicyExceptionAction.Block,
    !isOrgScope
  );
  const createOrgException = useCreateOrganizationPolicyException(
    organizationId as OrganizationId | undefined,
    policyKey
  );
  const updateOrgException = useUpdateOrganizationPolicyException(
    organizationId as OrganizationId | undefined,
    policyKey
  );
  const deleteOrgException = useDeleteOrganizationPolicyException(
    organizationId as OrganizationId | undefined,
    policyKey
  );
  const createUserException = useCreateUserPolicyException(policyKey);
  const updateUserException = useUpdateUserPolicyException(policyKey);
  const deleteUserException = useDeleteUserPolicyException(policyKey);

  const allowedExceptions = organizationId
    ? orgAllowedExceptions.data
    : userAllowedExceptions.data;
  const blockedExceptions = organizationId
    ? orgBlockedExceptions.data
    : userBlockedExceptions.data;
  const exceptionsLoading = isOrgScope
    ? orgAllowedExceptions.isLoading || orgBlockedExceptions.isLoading
    : userAllowedExceptions.isLoading || userBlockedExceptions.isLoading;

  const [state, setState] = useState<AllowBlockState>(() =>
    defaultAllowBlockState()
  );
  /** Last saved baseline — drives dirty detection and diff UI. */
  const [savedBaseline, setSavedBaseline] = useState<AllowBlockState>(() =>
    defaultAllowBlockState()
  );
  const [rulesHydrated, setRulesHydrated] = useState(false);
  const [tablesValid, setTablesValid] = useState(true);
  const tablesValidRef = useRef(true);
  const stateRef = useRef(state);
  const baselineRef = useRef(savedBaseline);
  /**
   * After a failed server sync, holds the form snapshot that failed. Auto-persist is
   * skipped while state still matches (avoids 409 / error loops). Cleared when the
   * user edits, or on the next successful persist.
   */
  const persistBlockedSnapshotRef = useRef<AllowBlockState | null>(null);
  const lastSentBlockedModeRef = useRef<PolicyModeType | null>(null);
  const blockedModeSaveFailedForRef = useRef<PolicyModeType | null>(null);
  /** Only one server sync at a time; extra triggers while in-flight are coalesced. */
  const persistInFlightRef = useRef(false);
  const persistQueuedRef = useRef(false);
  const persistAllowBlockRef = useRef<() => Promise<void>>(async () => {
    await Promise.resolve();
  });
  tablesValidRef.current = tablesValid;
  stateRef.current = state;
  baselineRef.current = savedBaseline;

  const hydrate = useCallback(async (): Promise<void> => {
    // Avoid overwriting in-progress user edits during query refetches.
    if (
      rulesHydrated &&
      (persistInFlightRef.current ||
        !statesEqual(stateRef.current, baselineRef.current))
    ) {
      return;
    }
    const defaultMode = defaultAllowBlockState().blockedListMode;
    const blockedListMode =
      blockedListModeFromPolicySettings(settingsData) ?? defaultMode;
    if (exceptionsLoading) {
      return;
    }
    const next = buildStateFromPolicyExceptions(
      policyKey,
      blockedListMode,
      allowedExceptions,
      blockedExceptions
    );
    setState(cloneAllowBlockState(next));
    setSavedBaseline(cloneAllowBlockState(next));
    setRulesHydrated(true);
  }, [
    allowedExceptions,
    blockedExceptions,
    exceptionsLoading,
    policyKey,
    rulesHydrated,
    settingsData,
  ]);

  useEffect(() => {
    setRulesHydrated(false);
  }, [organizationId, policyKey]);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  const persistBlockedListMode = useCallback(
    (nextMode: PolicyModeType): void => {
      if (readOnly) {
        return;
      }
      if (!onSave || !settingsData) {
        return;
      }
      if (nextMode === savedBaseline.blockedListMode) {
        return;
      }
      if (lastSentBlockedModeRef.current === nextMode) {
        return;
      }
      if (blockedModeSaveFailedForRef.current === nextMode) {
        return;
      }
      const payload = buildPolicySettingsPayload(settingsData, {
        blockedListMode: nextMode,
      });
      void Promise.resolve(onSave(policyKey, payload))
        .then(() => {
          lastSentBlockedModeRef.current = nextMode;
          blockedModeSaveFailedForRef.current = null;
        })
        .catch(() => {
          blockedModeSaveFailedForRef.current = nextMode;
        });
    },
    [onSave, policyKey, readOnly, savedBaseline.blockedListMode, settingsData]
  );

  const isDirty = useMemo(
    () => !statesEqual(state, savedBaseline),
    [state, savedBaseline]
  );

  useEffect(() => {
    onPolicySettingsDirty?.(isDirty);
  }, [isDirty, onPolicySettingsDirty]);

  useEffect(() => {
    if (!scope) return;
    if (isDirty) {
      markDirty(scope);
    } else {
      markClean(scope);
    }
    return () => {
      markClean(scope);
    };
  }, [isDirty, scope, markDirty, markClean]);

  const createException = useCallback(
    async (
      action: PolicyExceptionAction,
      ruleObject: Record<string, string>
    ): Promise<PolicyException> => {
      if (organizationId) {
        return createOrgException.mutateAsync({ action, ruleObject });
      }
      return createUserException.mutateAsync({ action, ruleObject });
    },
    [createOrgException, createUserException, organizationId]
  );

  const updateException = useCallback(
    async (
      exceptionId: string,
      action: PolicyExceptionAction,
      ruleObject: Record<string, string>
    ): Promise<PolicyException> => {
      if (organizationId) {
        return updateOrgException.mutateAsync({
          exceptionId,
          data: { action, ruleObject },
        });
      }
      return updateUserException.mutateAsync({
        exceptionId,
        data: { action, ruleObject },
      });
    },
    [organizationId, updateOrgException, updateUserException]
  );

  const deleteException = useCallback(
    async (exceptionId: string): Promise<void> => {
      if (organizationId) {
        await deleteOrgException.mutateAsync(exceptionId);
        return;
      }
      await deleteUserException.mutateAsync(exceptionId);
    },
    [deleteOrgException, deleteUserException, organizationId]
  );

  const syncRuleList = useCallback(
    async (
      action: PolicyExceptionAction,
      baselineRows: AllowBlockState['allowed'],
      nextRows: AllowBlockState['allowed']
    ): Promise<AllowBlockState['allowed']> => {
      const baselineActive = baselineRows.filter(row => !row.pendingDelete);
      const nextActive = nextRows.filter(row => !row.pendingDelete);
      const baselineById = new Map(baselineActive.map(row => [row.id, row]));
      const nextIds = new Set(nextActive.map(row => row.id));

      const toRemove = baselineActive.filter(row => !nextIds.has(row.id));
      await Promise.all(toRemove.map(row => deleteException(row.id)));

      const ruleObjectsEqual = (
        a: Record<string, string>,
        b: Record<string, string>
      ): boolean => {
        const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
        for (const k of keys) {
          if ((a[k] ?? '') !== (b[k] ?? '')) {
            return false;
          }
        }
        return true;
      };

      type UpsertResult = {
        index: number;
        rule: AllowBlockState['allowed'][0];
      };
      const upserted = await Promise.all(
        nextActive.map(async (row, index): Promise<UpsertResult> => {
          const previous = baselineById.get(row.id);
          const nextRuleObject = policyRuleToExceptionRuleObject(
            policyKey,
            row
          );
          if (!previous) {
            const created = await createException(action, nextRuleObject);
            return {
              index,
              rule: policyExceptionToPolicyRule(policyKey, created),
            };
          }
          const previousRuleObject = policyRuleToExceptionRuleObject(
            policyKey,
            previous
          );
          if (!ruleObjectsEqual(previousRuleObject, nextRuleObject)) {
            const updated = await updateException(
              row.id,
              action,
              nextRuleObject
            );
            return {
              index,
              rule: policyExceptionToPolicyRule(policyKey, updated),
            };
          }
          return {
            index,
            rule: {
              id: row.id,
              fields: { ...row.fields },
            },
          };
        })
      );
      upserted.sort((x, y) => x.index - y.index);
      return upserted.map(u => u.rule);
    },
    [createException, deleteException, policyKey, updateException]
  );

  const executePersistAllowBlock = useCallback(async (): Promise<void> => {
    if (readOnly) {
      return;
    }
    if (!tablesValidRef.current) {
      return;
    }
    const sourceState = stateRef.current;
    const sourceBaseline = baselineRef.current;
    const persisted = dropPendingDeletes(sourceState);
    let canonicalState = persisted;
    let syncedAllowed: AllowBlockState['allowed'] | null = null;
    try {
      syncedAllowed = await syncRuleList(
        PolicyExceptionAction.Allow,
        sourceBaseline.allowed,
        persisted.allowed
      );
      const syncedBlocked = await syncRuleList(
        PolicyExceptionAction.Block,
        sourceBaseline.blocked,
        persisted.blocked
      );
      canonicalState = {
        blockedListMode: persisted.blockedListMode,
        allowed: syncedAllowed,
        blocked: syncedBlocked,
      };
      persistBlockedSnapshotRef.current = null;
    } catch (error) {
      const recoveredAllowed = syncedAllowed;
      if (recoveredAllowed !== null) {
        setState(prev => ({
          ...prev,
          allowed: recoveredAllowed,
        }));
        setSavedBaseline({
          blockedListMode: persisted.blockedListMode,
          allowed: recoveredAllowed,
          blocked: sourceBaseline.blocked,
        });
      }
      const status = getApiErrorStatus(error);
      const isConflict = status === 409;
      const message =
        error instanceof Error ? error.message : 'Could not save changes.';
      toast({
        variant: 'destructive',
        title: isConflict ? 'Could not save rule' : 'Could not save lists',
        description: isConflict
          ? 'This rule already exists for this policy. Change or remove the duplicate row, then try again.'
          : message,
      });
      persistBlockedSnapshotRef.current = cloneAllowBlockState(sourceState);
      return;
    }
    const hasNewerEdits = !statesEqual(stateRef.current, sourceState);
    if (!hasNewerEdits) {
      // Separate deep clones so state vs baseline trees never alias (hydrate / reset).
      const clonedBaseline = cloneAllowBlockState(canonicalState);
      const clonedState = cloneAllowBlockState(canonicalState);
      // Queued persist can run in `finally` before React re-renders. Advance refs only
      // when state matches what we persisted; else server ids in baseline vs client ids
      // in stateRef would make the next sync DELETE then re-CREATE.
      baselineRef.current = clonedBaseline;
      stateRef.current = clonedState;
      setState(clonedState);
      onPolicySettingsDirty?.(false);
    }
    setSavedBaseline(cloneAllowBlockState(canonicalState));
  }, [onPolicySettingsDirty, readOnly, syncRuleList, toast]);

  const persistAllowBlock = useCallback(async (): Promise<void> => {
    if (persistInFlightRef.current) {
      persistQueuedRef.current = true;
      return;
    }
    persistInFlightRef.current = true;
    try {
      await executePersistAllowBlock();
    } finally {
      persistInFlightRef.current = false;
      if (persistQueuedRef.current) {
        persistQueuedRef.current = false;
        void persistAllowBlockRef.current();
      }
    }
  }, [executePersistAllowBlock]);

  persistAllowBlockRef.current = persistAllowBlock;

  useEffect(() => {
    if (!rulesHydrated || readOnly) {
      return;
    }
    if (!tablesValid) {
      return;
    }
    if (statesEqual(state, savedBaseline)) {
      return;
    }
    const blocked = persistBlockedSnapshotRef.current;
    if (blocked !== null) {
      if (!statesEqual(state, blocked)) {
        persistBlockedSnapshotRef.current = null;
      } else {
        return;
      }
    }
    const timer = window.setTimeout(() => {
      void persistAllowBlock();
    }, AUTO_PERSIST_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [
    persistAllowBlock,
    readOnly,
    rulesHydrated,
    savedBaseline,
    state,
    tablesValid,
  ]);

  const handleReset = useCallback(async (): Promise<void> => {
    if (readOnly) {
      return;
    }
    const baseline = baselineRef.current;
    const ids = [...baseline.allowed, ...baseline.blocked].map(row => row.id);
    const deleteResults = await Promise.allSettled(
      ids.map(id => deleteException(id))
    );
    const failedDelete = deleteResults.find(
      result => result.status === 'rejected'
    );
    if (failedDelete) {
      const message =
        failedDelete.reason instanceof Error
          ? failedDelete.reason.message
          : 'Could not delete all rules.';
      toast({
        variant: 'destructive',
        title: 'Could not reset rules',
        description: message,
      });
      return;
    }
    try {
      await Promise.resolve(onReset(policyKey));
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Could not reset settings.';
      toast({
        variant: 'destructive',
        title: 'Could not reset settings',
        description: message,
      });
      return;
    }
    persistBlockedSnapshotRef.current = null;
    lastSentBlockedModeRef.current = null;
    blockedModeSaveFailedForRef.current = null;
    const next = defaultAllowBlockState();
    setState(cloneAllowBlockState(next));
    setSavedBaseline(cloneAllowBlockState(next));
  }, [deleteException, onReset, policyKey, readOnly, toast]);

  if (isLoading || !settingsData || !rulesHydrated) {
    return (
      <div className="pt-4">
        <div className="text-sm text-muted-foreground">
          {isLoading
            ? 'Loading settings...'
            : !settingsData
              ? 'No settings available.'
              : 'Loading rules…'}
        </div>
      </div>
    );
  }

  const policy = settingsData.policy;
  const hasCustomized =
    state.allowed.some(row => !row.pendingDelete) ||
    state.blocked.some(row => !row.pendingDelete) ||
    state.blockedListMode !== PolicyMode.Monitor;

  return (
    <form className="pt-4 space-y-4">
      {policyKey === 'mcp-server-sources' ? <TrustedMcpServersPanel /> : null}
      <AllowBlockPolicyEditor
        policyKey={policyKey}
        value={state}
        onChange={nextState => {
          if (nextState.blockedListMode !== state.blockedListMode) {
            persistBlockedListMode(nextState.blockedListMode);
          }
          setState(nextState);
        }}
        savedBaseline={savedBaseline}
        readOnly={readOnly}
        isSupportReadOnly={isSupportReadOnly}
        supportsEnforce={policy.supportsEnforce ?? false}
        enforceDisabledReason={policy.enforceDisabledReason}
        enforceTooltip={policy.enforceTooltip}
        onFormValidityChange={setTablesValid}
      />
      <PolicySettingsActions
        hasCustomizedSettings={hasCustomized}
        policyName={policy.name}
        isDirty={isDirty}
        isSubmitting={saving}
        readOnly={readOnly}
        isValid={tablesValid}
        resetOnly
        onReset={() => void handleReset()}
      />
    </form>
  );
}
